import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BuyerBidDetailDto } from "../../../autogen/bff-api";
import { visibleLength } from "../../../common/text/visibleLength";
import { useBidRejectionDecision } from "./useBidRejectionDecision";

export interface UndoRejectionDialog {
  open: (onUndone?: () => void) => void;
  modal: ReactNode;
  canRevoke: boolean;
  lockedByAwardDecision: boolean;
  lockedByAward: boolean;
  isRejected: boolean;
  isLoading: boolean;
}

export const useUndoRejectionDialog = (detail: BuyerBidDetailDto): UndoRejectionDialog => {
  const rejection = useBidRejectionDecision(detail);
  const [request, setRequest] = useState<{ onUndone?: () => void } | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const open = (onUndone?: () => void) => {
    setFailure(null);
    setRequest({ onUndone });
  };

  const undo = async (reason: string) => {
    setFailure(null);
    const outcome = await rejection.revokeOutcome(reason);
    if (!outcome.ok) {
      setFailure(outcome.error);
      return;
    }
    const onUndone = request?.onUndone;
    setRequest(null);
    onUndone?.();
  };

  return {
    open,
    modal: request && (
      <UndoRejectionModal
        supplierName={detail.ownerName}
        isLoading={rejection.isLoading}
        failure={failure}
        onCancel={() => setRequest(null)}
        onUndo={undo}
      />
    ),
    canRevoke: rejection.canRevoke,
    lockedByAwardDecision: rejection.lockedByAwardDecision,
    lockedByAward: rejection.lockedByAward,
    isRejected: rejection.isRejected,
    isLoading: rejection.isLoading,
  };
};

const UndoRejectionModal = ({
  supplierName,
  isLoading,
  failure,
  onCancel,
  onUndo,
}: {
  supplierName: string | undefined;
  isLoading: boolean;
  failure: unknown;
  onCancel: () => void;
  onUndo: (reason: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const hasReason = visibleLength(reason) > 0;
  const tooLong = reason.trim().length > MAX_REVOKE_REASON_LENGTH;
  const reasonRefused = errorCodeOf(failure) === REASON_REQUIRED;

  return (
    <Modal isOpen onClose={onCancel} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Undo the rejection?")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Text>
              {t(
                "The tender from {{supplier}} counts as delivered again. The supplier is back in any open round and can be invited to later rounds.",
                { supplier: supplierName },
              )}
            </Text>
            <FormControl isRequired isInvalid={reasonRefused || tooLong}>
              <FormLabel>{t("Reason")}</FormLabel>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} />
              <FormHelperText>
                {t("Shown in the activity log and the protocol. The supplier does not see the reason.")}
              </FormHelperText>
              <FormHelperText mt="1">
                {t("{{count}} / {{max}} characters", { count: reason.trim().length, max: MAX_REVOKE_REASON_LENGTH })}
              </FormHelperText>
              <FormErrorMessage>
                {tooLong
                  ? t("The reason can be at most {{max}} characters.", { max: MAX_REVOKE_REASON_LENGTH })
                  : t(REASON_REQUIRED)}
              </FormErrorMessage>
            </FormControl>
            {failure != null && !reasonRefused && (
              <Alert status="warning" rounded="md" fontSize="sm">
                <AlertIcon />
                {errorCodeOf(failure) === LOCKED_BY_AWARD_DECISION
                  ? t("The award decision has been made. Change the award decision first, then the rejection can be undone.")
                  : errorCodeOf(failure) === QUALIFICATION_CLOSED
                    ? t("The procurement has ended, so the rejection can no longer be undone.")
                    : t("The rejection could not be undone. Try again.")}
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="2">
          <Button variant="ghost" onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button
            colorScheme="red"
            isDisabled={!hasReason || tooLong}
            isLoading={isLoading}
            onClick={() => void onUndo(reason.trim())}
          >
            {t("Undo the rejection")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const QUALIFICATION_CLOSED = "zz_BidApiErrorCode_QualificationClosed";
const REASON_REQUIRED = "zz_BidApiErrorCode_RevokeRejectionReasonRequired";
const MAX_REVOKE_REASON_LENGTH = 4000;
const LOCKED_BY_AWARD_DECISION = "zz_BidApiErrorCode_RejectionLockedByAwardDecision";

const errorCodeOf = (error: unknown): string | undefined =>
  (error as { data?: { errorCode?: string } } | null)?.data?.errorCode;
